import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Loader2, Plus, Send, Trash2, X } from "lucide-react";
import { API, auth, guarded, OFFICES, PRIORITIES, TEAM, TICKET_STATUSES, chipCls, inputCls, label, selectCls, btnPrimary } from "./shared";

const empty = { title: "", school: "", office: "", priority: "medium", description: "" };

export default function Tickets({ token, onAuthError }) {
  const [items, setItems] = useState(null);
  const [schools, setSchools] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [statusF, setStatusF] = useState("");
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(
    () =>
      guarded(async () => {
        const [{ data: t }, { data: s }] = await Promise.all([
          axios.get(`${API}/tickets`, auth(token)),
          axios.get(`${API}/schools`, auth(token)),
        ]);
        setItems(t);
        setSchools(s);
      }, onAuthError),
    [token, onAuthError]
  );

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = (e) => {
    e.preventDefault();
    guarded(async () => {
      await axios.post(`${API}/tickets`, form, auth(token));
      setForm(empty);
      setShowForm(false);
      await load();
    }, onAuthError);
  };

  const patch = (id, body) =>
    guarded(async () => {
      await axios.patch(`${API}/tickets/${id}`, body, auth(token));
      await load();
    }, onAuthError);

  const remove = (id) => {
    if (!window.confirm("Delete this ticket? This cannot be undone.")) return;
    guarded(async () => {
      await axios.delete(`${API}/tickets/${id}`, auth(token));
      await load();
    }, onAuthError);
  };

  if (!items) {
    return (
      <div className="flex items-center gap-3 py-20 text-[#8A837B]">
        <Loader2 size={18} className="animate-spin" /> Loading tickets…
      </div>
    );
  }

  const filtered = items.filter((t) => !statusF || t.status === statusF);

  return (
    <div data-testid="tickets-tab">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <select data-testid="tickets-filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className={selectCls}>
            <option value="">All statuses</option>
            {TICKET_STATUSES.map((s) => (
              <option key={s} value={s}>{label(s)}</option>
            ))}
          </select>
          <span className="text-xs text-[#8A837B]" data-testid="tickets-count">{filtered.length} ticket{filtered.length === 1 ? "" : "s"}</span>
        </div>
        <button data-testid="ticket-add-toggle" onClick={() => setShowForm(!showForm)} className={btnPrimary}>
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Close" : "New Ticket"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={add} data-testid="ticket-form" className="mb-8 border border-[#E6E2DC] bg-white p-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <input data-testid="ticket-title-input" required value={form.title} onChange={set("title")} placeholder="Issue title *" className={inputCls} />
            <select data-testid="ticket-school-select" value={form.school} onChange={set("school")} className={selectCls}>
              <option value="">School…</option>
              {schools.map((s) => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
            <select data-testid="ticket-office-select" value={form.office} onChange={set("office")} className={selectCls}>
              <option value="">Office…</option>
              {OFFICES.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <select data-testid="ticket-priority-select" value={form.priority} onChange={set("priority")} className={selectCls}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{label(p)}</option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="ticket-description-input"
            value={form.description}
            onChange={set("description")}
            placeholder="Describe the issue…"
            rows={3}
            className={`${inputCls} mt-5 resize-none`}
          />
          <button type="submit" data-testid="ticket-submit-button" className={`${btnPrimary} mt-6`}>
            <Plus size={14} /> Create Ticket
          </button>
        </form>
      )}

      <div className="border border-[#E6E2DC] bg-white">
        {filtered.length === 0 && <p className="p-8 text-sm text-[#8A837B]" data-testid="tickets-empty">No tickets — schools' issues will appear here.</p>}
        {filtered.map((t) => {
          const open = openId === t.id;
          return (
            <div key={t.id} className="border-b border-[#E6E2DC] last:border-b-0" data-testid={`ticket-row-${t.id}`}>
              <div className="grid grid-cols-2 items-center gap-3 px-5 py-4 md:grid-cols-12">
                <button
                  data-testid={`ticket-expand-${t.id}`}
                  onClick={() => setOpenId(open ? null : t.id)}
                  className="col-span-2 flex items-center gap-3 text-left md:col-span-4"
                >
                  <ChevronDown size={14} className={`shrink-0 text-[#8A837B] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                  <span>
                    <span className="block text-sm font-semibold text-[#141210]">{t.title}</span>
                    <span className="block text-xs text-[#6B655F]">{t.school || t.office || "—"}</span>
                  </span>
                </button>
                <span className={`inline-block w-fit px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] md:col-span-1 ${chipCls(t.priority)}`}>
                  {label(t.priority)}
                </span>
                <select
                  data-testid={`ticket-status-${t.id}`}
                  value={t.status}
                  onChange={(e) => patch(t.id, { status: e.target.value })}
                  className={`${selectCls} md:col-span-2`}
                >
                  {TICKET_STATUSES.map((s) => (
                    <option key={s} value={s}>{label(s)}</option>
                  ))}
                </select>
                <select
                  data-testid={`ticket-priority-${t.id}`}
                  value={t.priority}
                  onChange={(e) => patch(t.id, { priority: e.target.value })}
                  className={`${selectCls} md:col-span-2`}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>{label(p)}</option>
                  ))}
                </select>
                <select
                  data-testid={`ticket-assignee-${t.id}`}
                  value={t.assigned_to || ""}
                  onChange={(e) => patch(t.id, { assigned_to: e.target.value })}
                  className={`${selectCls} md:col-span-2`}
                >
                  <option value="">Unassigned</option>
                  {TEAM.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <button
                  data-testid={`ticket-delete-${t.id}`}
                  onClick={() => remove(t.id)}
                  className="justify-self-end text-[#8A837B] transition-colors duration-300 hover:text-[#581820] md:col-span-1"
                  aria-label="Delete ticket"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              {open && (
                <div className="border-t border-[#F1EDE6] bg-[#FAF8F5] px-5 py-5 md:pl-12" data-testid={`ticket-detail-${t.id}`}>
                  <p className="max-w-3xl text-sm leading-relaxed text-[#141210]">{t.description || "No description."}</p>
                  <p className="mt-2 text-xs text-[#8A837B]">Opened {new Date(t.created_at).toLocaleString()}</p>
                  <div className="mt-4">
                    <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A837B]">Notes</h4>
                    <div className="mt-2 flex flex-col gap-2">
                      {(t.notes || []).map((n, i) => (
                        <p key={i} className="text-xs text-[#141210]">
                          <span className="font-semibold">{n.by}</span> · {new Date(n.at).toLocaleString()} — {n.text}
                        </p>
                      ))}
                      {(t.notes || []).length === 0 && <p className="text-xs text-[#8A837B]">No notes yet.</p>}
                    </div>
                    <div className="mt-3 flex max-w-xl gap-2">
                      <input
                        data-testid={`ticket-note-input-${t.id}`}
                        value={open ? note : ""}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a note…"
                        className={inputCls}
                      />
                      <button
                        data-testid={`ticket-note-add-${t.id}`}
                        onClick={async () => {
                          if (!note.trim()) return;
                          await patch(t.id, { note: note.trim() });
                          setNote("");
                        }}
                        className="flex items-center gap-2 bg-[#141210] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#581820]"
                      >
                        <Send size={12} /> Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
